import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useMerchantScope } from '@/hooks/use-merchant-scope';
import { useServiceMap } from '@/hooks/use-service-map';
import {
  Button, Input, Label,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui';
import type { ComboboxOption } from '@/components/ui/combobox';
import { MerchantSelect } from '@/components/features/merchants/merchant-select';
import { MultiSelectFilter } from '@/components/shared/multi-select-filter';
import { DateRangePicker, type DateRange } from '@/components/shared/date-range-picker';
import {
  decodeFilterAxis,
  encodeFilterAxis,
  type FilterAxisValue,
} from '@/lib/utils/filter-axis';
import type { ListAdminSubscriptionsParams, SubscriptionStatus } from '@/types/admin-subscription';

// Spec 0222 B9: Facade-Enum — 'cancelled' existiert dort nicht.
const ALL_STATUSES: SubscriptionStatus[] = ['active', 'pending_termination', 'terminated'];
// Spec 410: Operator-Werte (im Subscription-Listen-Payload je Zeile, Contract-required).
const OPERATOR_IDS = ['D1_DE', 'D2_DE', 'O2_DE'] as const;
type RenewalsOp = 'eq' | 'gt';

/** `<op>:<n>` → Bedien-Zustand. Ungültiges → leerer Wert (Filter inaktiv). */
function parseRenewalsParam(raw: string | undefined): { op: RenewalsOp; value: string } {
  if (!raw) return { op: 'gt', value: '' };
  const idx = raw.indexOf(':');
  if (idx < 0) return { op: 'gt', value: '' };
  const op = raw.slice(0, idx).trim();
  const value = raw.slice(idx + 1).trim();
  return {
    op: op === 'eq' ? 'eq' : 'gt',
    value: /^\d+$/.test(value) ? value : '',
  };
}

/** Bedien-Zustand → `<op>:<n>` (oder undefined, wenn die Zahl leer/ungültig ist). */
function encodeRenewalsParam(op: RenewalsOp, value: string): string | undefined {
  const trimmed = value.trim();
  return /^\d+$/.test(trimmed) ? `${op}:${trimmed}` : undefined;
}

interface SubscriptionFiltersProps {
  /** Server-seitige Filter (resetten Cursor). */
  filters: ListAdminSubscriptionsParams;
  onFiltersChange: (filters: ListAdminSubscriptionsParams) => void;
  /** Client-seitige Datums-Filter (kein Cursor-Reset). */
  dateFrom?: Date;
  dateTo?: Date;
  onDateRangeChange: (range: DateRange) => void;
  /** Reset-Button nur zeigen, wenn irgendein Filter aktiv ist. */
  canReset?: boolean;
  onReset?: () => void;
}

export function SubscriptionFilters({
  filters,
  onFiltersChange,
  dateFrom,
  dateTo,
  onDateRangeChange,
  canReset = false,
  onReset,
}: SubscriptionFiltersProps) {
  const { t } = useTranslation('subscriptions');
  const { showMerchant } = useMerchantScope();
  // Spec 410: Service-Katalog für Optionen (Suche nach serviceId UND DisplayName) + Chip-Label.
  const { services, resolveServiceLabel } = useServiceMap();

  const serviceAxis = useMemo(() => decodeFilterAxis(filters.service), [filters.service]);
  const statusAxis = useMemo(() => decodeFilterAxis(filters.statuses), [filters.statuses]);
  const operatorAxis = useMemo(() => decodeFilterAxis(filters.operator), [filters.operator]);
  const renewals = useMemo(() => parseRenewalsParam(filters.renewals), [filters.renewals]);

  const serviceOptions = useMemo<ComboboxOption[]>(
    () =>
      services.map((s) => ({
        value: s.serviceId,
        label: resolveServiceLabel(s.serviceId),
        description: s.serviceId, // macht die Combobox-Suche nach der serviceId möglich
      })),
    [services, resolveServiceLabel],
  );

  const operatorOptions = useMemo<ComboboxOption[]>(
    () => OPERATOR_IDS.map((o) => ({ value: o, label: o })),
    [],
  );

  const statusOptions = useMemo<ComboboxOption[]>(
    () => ALL_STATUSES.map((s) => ({ value: s, label: t(`statuses.${s}`) })),
    [t],
  );

  const setServiceAxis = (axis: FilterAxisValue) =>
    onFiltersChange({ ...filters, service: encodeFilterAxis(axis), cursor: undefined });
  const setStatusAxis = (axis: FilterAxisValue) =>
    onFiltersChange({ ...filters, statuses: encodeFilterAxis(axis), cursor: undefined });
  const setOperatorAxis = (axis: FilterAxisValue) =>
    onFiltersChange({ ...filters, operator: encodeFilterAxis(axis), cursor: undefined });

  const handleMerchantChange = (value: string | undefined) =>
    onFiltersChange({ ...filters, merchantId: value, cursor: undefined });

  const handleSubIdChange = (value: string) =>
    // Spec 410: ein leerer/whitespace-only Wert zählt als abwesend (keine Facette).
    onFiltersChange({ ...filters, subId: value.trim() || undefined, cursor: undefined });

  const handleRenewalsOpChange = (op: string) =>
    onFiltersChange({
      ...filters,
      renewals: encodeRenewalsParam(op as RenewalsOp, renewals.value),
      cursor: undefined,
    });
  const handleRenewalsValueChange = (value: string) =>
    onFiltersChange({
      ...filters,
      renewals: encodeRenewalsParam(renewals.op, value),
      cursor: undefined,
    });

  return (
    <div className="mb-4 space-y-3" role="search">
      {/* Zeile 1: Mehrfach-Filter (Status, Service, Operator) + Merchant + ID-Suche */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="w-[240px]">
          <Label className="text-xs text-muted-foreground">{t('list.filters.status')}</Label>
          <MultiSelectFilter
            value={statusAxis}
            onChange={setStatusAxis}
            options={statusOptions}
            placeholder={t('list.filters.statusPlaceholder', 'Beliebiger Status')}
          />
        </div>

        {showMerchant && (
          <div>
            <Label className="text-xs text-muted-foreground">{t('list.filters.merchant', 'Merchant')}</Label>
            <MerchantSelect
              value={filters.merchantId}
              onChange={handleMerchantChange}
              className="w-[220px]"
            />
          </div>
        )}

        <div className="w-[260px]">
          <Label className="text-xs text-muted-foreground">{t('list.filters.service')}</Label>
          <MultiSelectFilter
            value={serviceAxis}
            onChange={setServiceAxis}
            options={serviceOptions}
            allowFreeText
            labelResolver={resolveServiceLabel}
            placeholder={t('list.filters.servicePlaceholder', 'Beliebiger Service')}
            searchPlaceholder={t('list.filters.serviceSearch', 'Nach ID oder Name suchen…')}
          />
        </div>

        <div className="w-[220px]">
          <Label className="text-xs text-muted-foreground">{t('list.filters.operator', 'Operator')}</Label>
          <MultiSelectFilter
            value={operatorAxis}
            onChange={setOperatorAxis}
            options={operatorOptions}
            placeholder={t('list.filters.operatorPlaceholder', 'Beliebiger Operator')}
          />
        </div>

        <div className="w-[200px]">
          <Label className="text-xs text-muted-foreground">{t('list.filters.subId', 'Abonnement-ID')}</Label>
          <Input
            placeholder={t('list.filters.subIdPlaceholder', 'Abonnement-ID suchen…')}
            value={filters.subId ?? ''}
            onChange={(e) => handleSubIdChange(e.target.value)}
          />
        </div>
      </div>

      {/* Zeile 2: Anzahl-Buchungen-Filter + Datum + Reset */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-muted-foreground">{t('list.filters.renewals', 'Anzahl Buchungen')}</Label>
          <div className="flex items-center gap-1.5">
            <Select value={renewals.op} onValueChange={handleRenewalsOpChange}>
              <SelectTrigger className="w-[130px]" aria-label={t('list.filters.renewals', 'Anzahl Buchungen')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gt">{t('list.filters.renewalsGt', 'größer als')}</SelectItem>
                <SelectItem value="eq">{t('list.filters.renewalsEq', 'gleich')}</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number"
              min="0"
              step="1"
              placeholder={t('list.filters.renewalsPlaceholder', 'Anzahl')}
              value={renewals.value}
              onChange={(e) => handleRenewalsValueChange(e.target.value)}
              className="w-[100px]"
            />
          </div>
        </div>

        <DateRangePicker from={dateFrom} to={dateTo} onChange={onDateRangeChange} />

        {canReset && onReset && (
          <Button variant="ghost" size="sm" onClick={onReset} className="h-9 text-muted-foreground">
            <X className="mr-1.5 h-4 w-4" />
            {t('list.filters.reset', 'Filter zurücksetzen')}
          </Button>
        )}
      </div>
    </div>
  );
}
